import db from "../db/database";
import { COUNTRY_CODES } from "../constants/countries";
import { fetchFromBusinessCentral } from "../utils/businessCentralUtils";
import { isOnline } from "./syncService";
import { anotarError } from "./syncLog";

/**
 * Configuración contable de Business Central (OData `ConfContabilidad`). Sólo
 * Perú.
 *
 * De ella sólo se usa el impuesto al consumo de bolsas de plástico: la cuenta,
 * su nombre y el importe por unidad. Ver `utils/impuestoDeBolsas.js`.
 *
 * Es una configuración de una sola fila: se lee entera y se reemplaza.
 */

/** El nombre de la tabla en Dexie y de la entidad en `syncMetadata`. */
export const ENTIDAD = "confContabilidad";

/** El servicio web de Business Central. */
export const SERVICIO = "ConfContabilidad";

// Los campos se mapean uno por uno, sin spread: si BC renombra uno, que falte a
// la vista y no que se cuele con otro nombre.
export const mapConfContabilidadFromBC = (fila) => ({
  Account_Tax_Bag: String(fila?.Account_Tax_Bag ?? "").trim(),
  Name_Account_Tax_Bag: String(fila?.Name_Account_Tax_Bag ?? "").trim(),
  Unit_Tax_Plastic_Bag: Number(fila?.Unit_Tax_Plastic_Bag) || 0,
  lastSynced: new Date(),
});

export const syncConfContabilidad = async () => {
  if (!isOnline()) {
    return false;
  }

  try {
    const leidas = await fetchFromBusinessCentral(SERVICIO);
    const filas = (Array.isArray(leidas) ? leidas : []).map(mapConfContabilidadFromBC);

    // Se reemplaza sólo después de leer bien: un fallo de red a mitad no debe
    // dejar al terminal sin el importe del impuesto.
    await db.transaction("rw", db.confContabilidad, async () => {
      await db.confContabilidad.clear();
      if (filas.length) {
        await db.confContabilidad.bulkAdd(filas.slice(0, 1));
      }
    });

    return true;
  } catch (error) {
    anotarError(ENTIDAD, "falló la lectura", error);
    return false;
  }
};

/** La configuración contable guardada, o `null` si no se sincronizó. */
export const getConfContabilidad = async () =>
  (await db.confContabilidad.toCollection().first()) ?? null;

/**
 * Lo que una línea nueva tiene que guardar del impuesto de bolsas: el importe
 * y la cuenta vigentes al agregarla, para que el ticket de una venta vieja no
 * cambie si mañana cambia la configuración.
 *
 * Devuelve `{}` si el producto no lleva el impuesto o el país no es Perú, y
 * `sinConfiguracion: true` si lo lleva pero no hay importe con qué calcularlo
 * --la línea del impuesto no se puede armar y hay que avisar--.
 *
 * @param {object} product     El producto, con `Plastic_Bag`.
 * @param {string} countryCode El código del país activo.
 */
export const camposDeImpuestoDeBolsa = async (product, countryCode) => {
  if (countryCode !== COUNTRY_CODES.PERU || product?.Plastic_Bag !== true) {
    return { campos: {}, sinConfiguracion: false };
  }

  const conf = await getConfContabilidad();
  const unitario = Number(conf?.Unit_Tax_Plastic_Bag) || 0;

  return {
    campos: {
      Plastic_Bag: true,
      Unit_Tax_Plastic_Bag: unitario,
      Account_Tax_Bag: conf?.Account_Tax_Bag || "",
      Name_Account_Tax_Bag: conf?.Name_Account_Tax_Bag || "",
    },
    sinConfiguracion: unitario <= 0,
  };
};
